import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Select } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Spinner } from 'components/ui';
import { TAG_CREATE, TAG_DELETE } from 'utils/access-control/rules';
import { RenderWithAccess } from 'utils/usePermissions';

import { useTags } from './useTags';

const StyledSelect = styled(Select)`
  min-width: 10rem;
  .ant-select-selection-item-content {
    button {
      display: none;
    }
  }
`;

const StyledOption = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export default function TagChooser({ onOpenChange, ...props }: any) {
  const { t } = useTranslation();
  const [removeId, setRemoveId] = useState<string | null>(null);
  // Keep search text across selections so users can bulk-select from a filtered list.
  // Clear when the dropdown closes (done selecting / dismissed the field).
  const [searchValue, setSearchValue] = useState('');
  const { tags, loading, addTag, removeTag, addLoading, deleteLoading } = useTags();

  async function remove(e: React.MouseEvent, tagId: string) {
    e.stopPropagation();
    setRemoveId(tagId);
    await removeTag(tagId);
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
    onOpenChange?.(open);
  };

  return (
    <StyledSelect
      mode="multiple"
      placeholder={t('settings.addTags', 'Add Tags...')}
      loading={loading}
      notFoundContent={t('settings.noExistingTags', 'No existing tags found.')}
      popupMatchSelectWidth={false}
      popupRender={menu => {
        const newEntry = !tags.some(({ tag }) => tag === searchValue) && searchValue;
        return (
          <>
            {menu}
            {newEntry && (
              <RenderWithAccess rules={[TAG_CREATE]}>
                <>
                  <Divider style={{ margin: '0' }} />
                  {addLoading ? (
                    <Spinner />
                  ) : (
                    <Button
                      style={{ margin: '0.25rem' }}
                      onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                      onClick={() => addTag(searchValue)}
                    >
                      <PlusOutlined />{' '}
                      {t('settings.createTag', 'Create "{{tag}}" tag', { tag: searchValue })}
                    </Button>
                  )}
                </>
              </RenderWithAccess>
            )}
          </>
        );
      }}
      {...props}
      autoClearSearchValue={false}
      searchValue={searchValue}
      onSearch={setSearchValue}
      onOpenChange={handleOpenChange}
      options={tags.map(({ id, tag }) => ({
        value: tag ?? '',
        label: (
          <StyledOption>
            {tag}
            <RenderWithAccess rules={[TAG_DELETE]}>
              <Button
                onClick={(e: React.MouseEvent) => remove(e, id)}
                style={{ padding: '0 0.5rem', height: '20px' }}
                loading={deleteLoading && id === removeId}
              >
                <CloseOutlined
                  style={{
                    fontSize: '10px',
                    lineHeight: '10px',
                    verticalAlign: 'middle',
                    marginTop: '-4px',
                  }}
                />
              </Button>
            </RenderWithAccess>
          </StyledOption>
        ),
      }))}
    />
  );
}
